import React, { useEffect, useRef, useCallback, useState } from "react";
import { Footer, Navbar } from "../components";
import api from "../utils/base_url";
import { useNavigate } from "react-router-dom";
import "../styles/AboutPage.css";

/* ═══════════════════════════════════════════════════════════
   SCROLL REVEAL HOOK
═══════════════════════════════════════════════════════════ */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add("in"); obs.disconnect(); } },
      { threshold: 0.06 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

/* ═══════════════════════════════════════════════════════════
   SVG — MEHNDI MANDALA (hero signature element)
   Intricate geometric mandala inspired by henna art
═══════════════════════════════════════════════════════════ */
const MandalaHero = () => (
  <svg
    className="sw-hero-mandala"
    viewBox="0 0 600 600"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Outer ring */}
    <circle cx="300" cy="300" r="280" stroke="#B8860B" strokeWidth="0.8" />
    <circle cx="300" cy="300" r="265" stroke="#B8860B" strokeWidth="0.4" />
    {/* Petal ring outer */}
    {Array.from({ length: 16 }).map((_, i) => {
      const a = (i / 16) * 360;
      const rad = (a * Math.PI) / 180;
      const cx = 300 + Math.cos(rad) * 255;
      const cy = 300 + Math.sin(rad) * 255;
      return <circle key={i} cx={cx} cy={cy} r="12" stroke="#B8860B" strokeWidth="0.8" fill="none" />;
    })}
    {/* Diamond ring */}
    {Array.from({ length: 16 }).map((_, i) => {
      const a = ((i / 16) * 360) + 11.25;
      const rad = (a * Math.PI) / 180;
      const cx = 300 + Math.cos(rad) * 245;
      const cy = 300 + Math.sin(rad) * 245;
      return (
        <polygon
          key={i}
          points={`${cx},${cy - 6} ${cx + 6},${cy} ${cx},${cy + 6} ${cx - 6},${cy}`}
          stroke="#9A6F07"
          strokeWidth="0.6"
          fill="none"
        />
      );
    })}
    {/* Mid ring */}
    <circle cx="300" cy="300" r="200" stroke="#9A6F07" strokeWidth="1" />
    {/* 8-point star from mid ring */}
    {Array.from({ length: 8 }).map((_, i) => {
      const a1 = (i / 8) * 360;
      const a2 = a1 + 22.5;
      const r1 = (a1 * Math.PI) / 180;
      const r2 = (a2 * Math.PI) / 180;
      return (
        <line key={i}
          x1={300 + Math.cos(r1) * 200}
          y1={300 + Math.sin(r1) * 200}
          x2={300 + Math.cos(r2) * 160}
          y2={300 + Math.sin(r2) * 160}
          stroke="#B8860B" strokeWidth="0.7" />
      );
    })}
    {/* Petal ring inner */}
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * 360;
      const rad = (a * Math.PI) / 180;
      const cx = 300 + Math.cos(rad) * 185;
      const cy = 300 + Math.sin(rad) * 185;
      return (
        <ellipse key={i}
          cx={cx} cy={cy} rx="10" ry="18"
          transform={`rotate(${a + 90}, ${cx}, ${cy})`}
          stroke="#C49A15" strokeWidth="0.6" fill="none" />
      );
    })}
    {/* Spokes */}
    {Array.from({ length: 24 }).map((_, i) => {
      const a = (i / 24) * 360;
      const rad = (a * Math.PI) / 180;
      return (
        <line key={i}
          x1={300 + Math.cos(rad) * 115}
          y1={300 + Math.sin(rad) * 115}
          x2={300 + Math.cos(rad) * 160}
          y2={300 + Math.sin(rad) * 160}
          stroke="#9A6F07" strokeWidth="0.5" />
      );
    })}
    {/* Inner circles */}
    <circle cx="300" cy="300" r="120" stroke="#B8860B" strokeWidth="0.9" />
    <circle cx="300" cy="300" r="100" stroke="#9A6F07" strokeWidth="0.5" strokeDasharray="4 4" />
    {/* 8 lotus petals */}
    {Array.from({ length: 8 }).map((_, i) => {
      const a = (i / 8) * 360;
      const rad = (a * Math.PI) / 180;
      const cx = 300 + Math.cos(rad) * 78;
      const cy = 300 + Math.sin(rad) * 78;
      return (
        <ellipse key={i}
          cx={cx} cy={cy} rx="16" ry="28"
          transform={`rotate(${a + 90}, ${cx}, ${cy})`}
          stroke="#C49A15" strokeWidth="0.8" fill="none" />
      );
    })}
    {/* Star polygon */}
    <polygon
      points="300,240 313,285 360,285 323,311 336,356 300,330 264,356 277,311 240,285 287,285"
      stroke="#B8860B" strokeWidth="0.8" fill="none" />
    {/* Core */}
    <circle cx="300" cy="300" r="42" stroke="#B8860B" strokeWidth="1.2" />
    <circle cx="300" cy="300" r="30" stroke="#C49A15" strokeWidth="0.8" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * 360;
      const rad = (a * Math.PI) / 180;
      return (
        <circle key={i}
          cx={300 + Math.cos(rad) * 36}
          cy={300 + Math.sin(rad) * 36}
          r="3.5" stroke="#B8860B" strokeWidth="0.6" fill="none" />
      );
    })}
    <circle cx="300" cy="300" r="10" stroke="#B8860B" strokeWidth="1" />
    <circle cx="300" cy="300" r="4" fill="#B8860B" opacity="0.6" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   SVG — PATTERN CARD ART (story section)
   Paisley + border pattern
═══════════════════════════════════════════════════════════ */
const PatternArt = () => (
  <svg viewBox="0 0 320 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="sw-story-pattern-svg">
    {/* Border frame */}
    <rect x="12" y="12" width="296" height="376" stroke="#B8860B" strokeWidth="1" rx="4" />
    <rect x="20" y="20" width="280" height="360" stroke="#9A6F07" strokeWidth="0.5" rx="3" />
    {/* Corner ornaments */}
    {[[24, 24], [296, 24], [24, 376], [296, 376]].map(([cx, cy], i) => (
      <g key={i}>
        <polygon points={`${cx},${cy - 8} ${cx + 8},${cy} ${cx},${cy + 8} ${cx - 8},${cy}`} stroke="#B8860B" strokeWidth="0.8" fill="none" />
        <circle cx={cx} cy={cy} r="2.5" fill="#B8860B" opacity="0.5" />
      </g>
    ))}
    {/* Central mandala mini */}
    <circle cx="160" cy="180" r="80" stroke="#9A6F07" strokeWidth="0.8" />
    <circle cx="160" cy="180" r="60" stroke="#B8860B" strokeWidth="0.6" />
    {Array.from({ length: 8 }).map((_, i) => {
      const a = (i / 8) * 360;
      const rad = (a * Math.PI) / 180;
      const cx = 160 + Math.cos(rad) * 48;
      const cy = 180 + Math.sin(rad) * 48;
      return <ellipse key={i} cx={cx} cy={cy} rx="8" ry="16"
        transform={`rotate(${a + 90}, ${cx}, ${cy})`}
        stroke="#C49A15" strokeWidth="0.7" fill="none" />;
    })}
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i / 12) * 360;
      const rad = (a * Math.PI) / 180;
      return <line key={i}
        x1={160 + Math.cos(rad) * 25}
        y1={180 + Math.sin(rad) * 25}
        x2={160 + Math.cos(rad) * 55}
        y2={180 + Math.sin(rad) * 55}
        stroke="#9A6F07" strokeWidth="0.4" />;
    })}
    <circle cx="160" cy="180" r="20" stroke="#B8860B" strokeWidth="0.8" />
    <circle cx="160" cy="180" r="6" fill="#B8860B" opacity="0.4" />
    {/* Paisley shapes */}
    {[
      [160, 60], [160, 310],
      [60, 180], [264, 180],
    ].map(([px, py], i) => {
      const isVert = i < 2;
      return (
        <ellipse key={i}
          cx={px} cy={py}
          rx={isVert ? 12 : 20}
          ry={isVert ? 22 : 12}
          stroke="#9A6F07" strokeWidth="0.6" fill="none" />
      );
    })}
    {/* Border dot pattern */}
    {Array.from({ length: 14 }).map((_, i) => (
      <circle key={i}
        cx={28 + i * 20} cy="32"
        r="1.5" fill="#B8860B" opacity="0.4" />
    ))}
    {Array.from({ length: 14 }).map((_, i) => (
      <circle key={i}
        cx={28 + i * 20} cy="368"
        r="1.5" fill="#B8860B" opacity="0.4" />
    ))}
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   SVG — PHIL ICONS
═══════════════════════════════════════════════════════════ */
const IconCraft = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="26" fill="rgba(184,134,11,0.1)" />
    {/* Stylised pencil drawing a curve */}
    <path d="M14 38 Q20 22 38 16" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <path d="M36 12 L40 16 L28 28 L24 28 L24 24 Z" stroke="#B8860B" strokeWidth="1.2" fill="none" />
    <circle cx="26" cy="26" r="3" fill="#B8860B" opacity="0.4" />
  </svg>
);

const IconPrecision = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="26" fill="rgba(184,134,11,0.1)" />
    {/* Cross-hair / target */}
    <circle cx="26" cy="26" r="13" stroke="#B8860B" strokeWidth="1.2" />
    <circle cx="26" cy="26" r="7" stroke="#9A6F07" strokeWidth="1" />
    <circle cx="26" cy="26" r="2.5" fill="#B8860B" />
    <line x1="26" y1="10" x2="26" y2="18" stroke="#B8860B" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="26" y1="34" x2="26" y2="42" stroke="#B8860B" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="10" y1="26" x2="18" y2="26" stroke="#B8860B" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="34" y1="26" x2="42" y2="26" stroke="#B8860B" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const IconHeritage = () => (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
    <circle cx="26" cy="26" r="26" fill="rgba(184,134,11,0.1)" />
    {/* Paisley */}
    <ellipse cx="26" cy="22" rx="8" ry="12" stroke="#B8860B" strokeWidth="1.2" fill="none" />
    <circle cx="26" cy="16" r="3" stroke="#9A6F07" strokeWidth="1" fill="none" />
    <path d="M20 34 Q26 38 32 34" stroke="#B8860B" strokeWidth="1" strokeLinecap="round" fill="none" />
    <circle cx="26" cy="36" r="1.5" fill="#B8860B" opacity="0.5" />
  </svg>
);

/* Ornament divider */
const Ornament = () => (
  <div className="sw-ornament">
    <div className="sw-ornament-line rev" />
    <div className="sw-ornament-dot" />
    <div className="sw-ornament-diamond" />
    <div className="sw-ornament-dot" />
    <div className="sw-ornament-line" />
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const AboutPage = () => {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const go = useCallback((id) => navigate("/product", { state: { categoryId: id } }), [navigate]);

  useEffect(() => {
    api.get("/categories/")
      .then(r => { if (r.data?.data) setCategories(r.data.data); })
      .catch(console.error);
  }, []);

  const repeated = [...categories, ...categories, ...categories];

  /* Reveal refs */
  const storyRef    = useReveal();
  const philRef     = useReveal();
  const processRef  = useReveal();
  const whyRef      = useReveal();
  const manifestoRef = useReveal();

  const philosophy = [
    {
      Icon: IconCraft,
      title: "Crafted by Hand",
      desc: "Every stencil design is hand-sketched by our artisans before being precision-cut — preserving the warmth of traditional mehndi art.",
    },
    {
      Icon: IconPrecision,
      title: "Laser Precision",
      desc: "Our production combines artisan design with modern laser-cutting to deliver clean, reusable stencils that never bleed or slip.",
    },
    {
      Icon: IconHeritage,
      title: "Rooted in Heritage",
      desc: "Motifs drawn from Rajasthani, Arabic, and South Indian henna traditions — curated so every pattern carries cultural meaning.",
    },
  ];

  const steps = [
    { num: "I",   title: "Sketch & Design", desc: "Artisans hand-draw motifs inspired by regional henna traditions." },
    { num: "II",  title: "Refine & Digitise", desc: "Designs are refined digitally, preserving organic curves and fine detail." },
    { num: "III", title: "Precision Cut",    desc: "Laser-cutting ensures every curve and dot lands exactly right." },
    { num: "IV",  title: "Quality Check",   desc: "Each stencil is tested for reusability, flex, and clean release." },
  ];

  const trust = [
    { icon: "✦", value: "10,000+", label: "Stencils Sold" },
    { icon: "◈", value: "54",      label: "Signature Collections" },
    { icon: "❋", value: "100%",    label: "Reusable Material" },
    { icon: "◇", value: "4.9★",    label: "Average Rating" },
  ];

  const why = [
    {
      num: "01",
      title: "No Mehndi Skill Required",
      desc: "Our stencils are designed so anyone — from beginners to professionals — can achieve intricate, consistent patterns in minutes.",
    },
    {
      num: "02",
      title: "54 Signature Patterns",
      desc: "We don't sell generic patterns. Each of our 54 collections is a curated set with cultural provenance and aesthetic cohesion.",
    },
    {
      num: "03",
      title: "Flexible & Reusable",
      desc: "Made from food-grade, skin-safe mylar — flexible enough to conform to hands, wrists, and feet, and reusable up to 50 times.",
    },
    {
      num: "04",
      title: "Ships Across India",
      desc: "From bridal prep in Mumbai to festival henna in Chennai — we deliver to every pin code with careful, damage-free packaging.",
    },
    {
      num: "05",
      title: "Designed in India",
      desc: "Every single motif originates from our in-house design studio — no licensed clip-art, no generic imports.",
    },
    {
      num: "06",
      title: "Community & Tutorials",
      desc: "Every order comes with access to our step-by-step henna tutorials, so you master the technique fast.",
    },
  ];

  return (
    <>
      <Navbar />
      <main className="sw-about">

        {/* ══════════ HERO ══════════ */}
        <section className="sw-hero">
          <div className="sw-hero-texture" />
          <MandalaHero />

          <div className="sw-hero-content">
            <div className="sw-hero-overline">
              <div className="sw-hero-overline-dash" />
              Established with Purpose
              <div className="sw-hero-overline-dash" />
            </div>

            <h1 className="sw-hero-wordmark">
              Stencils <span>World</span> 54
            </h1>
            <p className="sw-hero-subtitle">Your Style, Our Stencil</p>

            <p className="sw-hero-tagline">
              Where ancient mehndi art meets the precision of modern craft —
              beautiful patterns, no experience needed.
            </p>

            <div className="sw-hero-cta-row">
              <a href="/product" className="sw-btn-gold">Explore Collections</a>
              <a href="#sw-story" className="sw-btn-ghost">Our Story</a>
            </div>
          </div>

          <div className="sw-hero-scroll">
            <div className="sw-hero-scroll-line" />
            <span className="sw-hero-scroll-text">Scroll</span>
          </div>

          <svg aria-hidden className="sw-hero-wave" viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,0 C480,60 960,0 1440,60 L1440,60 L0,60Z" fill="var(--bg-page)" />
          </svg>
        </section>

        {/* ══════════ BRAND STORY ══════════ */}
        <section id="sw-story" ref={storyRef} className="sw-story sw-reveal">
          <div className="sw-story-inner">
            {/* Left — decorative pattern card */}
            <div className="sw-story-left">
              <div className="sw-story-left-number" aria-hidden>54</div>
              <div className="sw-story-pattern-card">
                <PatternArt />
                <div className="sw-story-pattern-label">
                  <p className="sw-story-pattern-label-text">
                    Every line is a story — every pattern, a tradition.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — text */}
            <div className="sw-story-right">
              <span className="sw-eyebrow gold">Our Story</span>
              <h2 className="sw-heading dark xl">The Art That<br /><em>Lives on Skin</em></h2>
              <Ornament />

              <blockquote className="sw-story-pull-quote">
                "Henna is not decoration — it is a language. We built Stencils World 54
                to let every hand speak it fluently."
              </blockquote>

              <p className="sw-body dark">
                Stencils World 54 was born from a simple frustration: beautiful mehndi always
                felt out of reach for anyone who hadn't spent years mastering the art. We believed
                that was wrong. Henna belongs to everyone — to the bride on her wedding morning,
                to the daughter celebrating Eid, to the artist who simply loves pattern.
              </p>
              <p className="sw-body dark">
                We spent two years obsessing over the geometry of traditional motifs — Rajasthani
                florals, Arabic paisleys, South Indian temple borders — and translating them into
                stencils precise enough to apply in minutes, yet rich enough to feel genuinely
                hand-drawn. The result is our signature library of 54 collections.
              </p>
              <p className="sw-body dark" style={{ marginBottom: 0 }}>
                The number 54 is not arbitrary. It is the count of our founding pattern
                families — each one a complete world of mehndi art with its own origin story,
                cultural reference, and aesthetic DNA.
              </p>
            </div>
          </div>
        </section>

        {/* ══════════ PHILOSOPHY ══════════ */}
        <section ref={philRef} className="sw-philosophy sw-reveal">
          <div className="sw-philosophy-inner">
            <div style={{ textAlign: "center", marginBottom: "var(--space-4)" }}>
              <span className="sw-eyebrow ivory">Our Philosophy</span>
              <h2 className="sw-heading light xl">
                Tradition. Precision. <em style={{ color: "var(--primary-400)" }}>Beauty.</em>
              </h2>
              <Ornament />
            </div>

            <div className="sw-philosophy-grid">
              {philosophy.map(({ Icon, title, desc }, i) => (
                <div key={i} className="sw-phil-card">
                  <div className="sw-phil-icon"><Icon /></div>
                  <p className="sw-phil-title">{title}</p>
                  <p className="sw-phil-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ CRAFT PROCESS ══════════ */}
        <section ref={processRef} className="sw-process sw-reveal">
          <div className="sw-process-inner">
            <div className="sw-process-header">
              <span className="sw-eyebrow gold">How It's Made</span>
              <h2 className="sw-heading dark xl">From Sketch to <em>Skin</em></h2>
              <Ornament />
              <p className="sw-body dark" style={{ textAlign: "center" }}>
                Every stencil passes through four stages of craft before it reaches you.
              </p>
            </div>

            <div className="sw-process-steps">
              {steps.map(({ num, title, desc }, i) => (
                <div key={i} className="sw-step">
                  <div className="sw-step-num-wrap">
                    <div className="sw-step-num-bg">
                      <span className="sw-step-num">{num}</span>
                    </div>
                  </div>
                  <p className="sw-step-title">{title}</p>
                  <p className="sw-step-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ TRUST STRIP ══════════ */}
        <div className="sw-trust">
          <div className="sw-trust-inner">
            {trust.map(({ icon, value, label }, i) => (
              <div key={i} className="sw-trust-item">
                <div className="sw-trust-icon">{icon}</div>
                <p className="sw-trust-value">{value}</p>
                <p className="sw-trust-label">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════ COLLECTIONS MARQUEE ══════════ */}
        {categories.length > 0 && (
          <section className="sw-collections">
            <div className="sw-collections-head">
              <span className="sw-eyebrow gold" style={{ display: "block", textAlign: "center" }}>Browse</span>
              <h2 className="sw-heading dark xl" style={{ textAlign: "center" }}>
                Our <em>Collections</em>
              </h2>
              <Ornament />
            </div>

            <div className="sw-marquee-track">
              <div className="sw-marquee">
                {repeated.map((cat, i) => (
                  <div key={i} className="sw-cat-tile" onClick={() => go(cat.id)}>
                    <div className="sw-cat-tile-img">
                      <img
                        src={cat.category_image || "https://via.placeholder.com/300x210"}
                        alt={cat.category_name}
                      />
                    </div>
                    <div className="sw-cat-tile-label">
                      <p className="sw-cat-tile-name">{cat.category_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ══════════ WHY US ══════════ */}
        <section ref={whyRef} className="sw-why sw-reveal">
          <div className="sw-why-inner">
            <div className="sw-why-header">
              <span className="sw-eyebrow gold" style={{ display: "block", textAlign: "center" }}>Why Stencils World 54</span>
              <h2 className="sw-heading dark xl" style={{ textAlign: "center" }}>
                Six Reasons Mehndi Artists<br /><em>Choose Us</em>
              </h2>
              <Ornament />
            </div>

            <div className="sw-why-grid">
              {why.map(({ num, title, desc }, i) => (
                <div key={i} className="sw-why-card">
                  <div className="sw-why-card-num" aria-hidden>{num}</div>
                  <p className="sw-why-card-title">{title}</p>
                  <p className="sw-why-card-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ CLOSING MANIFESTO ══════════ */}
        <section ref={manifestoRef} className="sw-manifesto sw-reveal">
          <div className="sw-manifesto-inner">
            <span className="sw-manifesto-quote-mark" aria-hidden>"</span>
            <p className="sw-manifesto-text">
              Mehndi has adorned hands at every celebration, every ritual, every milestone
              for <em>five thousand years</em>. We exist to make sure that beauty
              never belongs only to the few who mastered the cone.
            </p>
            <div className="sw-manifesto-sig">
              <div className="sw-manifesto-sig-line" />
              Stencils World 54
              <div className="sw-manifesto-sig-line" />
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default AboutPage;